import { useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { Outlet } from 'react-router'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { RoleBase } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import {
	AccountRolesParamsType,
	useAccountRoleList,
	useAccountRoleRemove,
} from '../api/account-roles'
import { Button, ButtonLink } from '../components/button'
import { Input } from '../components/input'
import { Paper } from '../components/paper'
import { Sidepanel, SidepanelContent, SidepanelFooter } from '../components/sidepanel'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'

export function AccountRoles() {
	const [params, setParams] = useList()
	const [filter, setFilter] = useState<AccountRolesParamsType>({})

	const query = useAccountRoleList({ ...params, ...filter })
	const remove = useAccountRoleRemove()
	const canEditAccountRoles = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_ACCOUNT_ROLES)
	const canDeleteAccountRoles = useHasAccountPermission(ACCOUNT_PERMISSION.DELETE_ACCOUNT_ROLES)

	const onRemove = (id: string) => {
		if (confirm('Opravdu chcete smazat roli?')) {
			remove.mutate(id)
		}
	}

	return (
		<Paper>
			<div className="flex justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
				<h3 className="text-md font-bold">Seznam rolí</h3>
				<DialogTrigger>
					<Button variant="SECONDARY">Filrovat</Button>
					<Sidepanel heading="Filtrovat">
						{({ close }) => (
							<AccountRolesSidepanel
								onClose={close}
								filter={filter}
								onSubmit={setFilter}
							/>
						)}
					</Sidepanel>
				</DialogTrigger>
			</div>
			<List<RoleBase>
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<ListHeader sortBy="id">ID</ListHeader>
						<ListHeader sortBy="name">Název</ListHeader>
						<th></th>
					</tr>
				}
			>
				{role => (
					<tr key={role.id}>
						<td width="10%">{role.id}</td>
						<td width="60%">{role.name}</td>
						<td width="30%" className="space-x-2">
							<ButtonLink
								isDisabled={!canEditAccountRoles}
								variant="SECONDARY"
								size="SMALL"
								to={role.id}
							>
								Upravit
							</ButtonLink>
							<Button
								isDisabled={!canDeleteAccountRoles}
								variant="DANGER"
								size="SMALL"
								onPress={() => onRemove(role.id)}
							>
								Smazat
							</Button>
						</td>
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}

function AccountRolesSidepanel({
	filter,
	onClose,
	onSubmit,
}: {
	filter: AccountRolesParamsType
	onClose: VoidFunction
	onSubmit: (filter: AccountRolesParamsType) => void
}) {
	const [id, setId] = useState<string>(filter.id ?? '')
	const [name, setName] = useState<string>(filter.name ?? '')

	return (
		<>
			<SidepanelContent className="space-y-4">
				<Input label="ID" value={id} onChange={setId}></Input>
				<Input label="Název" value={name} onChange={setName}></Input>
			</SidepanelContent>
			<SidepanelFooter>
				<Button
					variant="PRIMARY"
					onPress={() => {
						onClose()
						onSubmit({ id, name })
					}}
				>
					Povrdit
				</Button>
				<Button
					variant="TEXT"
					size="TEXT"
					onPress={() => {
						onClose()
						onSubmit({})
					}}
				>
					Odstranit vše
				</Button>
			</SidepanelFooter>
		</>
	)
}
